package co.maytes.standupbot.dashboard.features.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideAlertTriangle
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.features.setup.ReconnectRoutes
import co.maytes.standupbot.dashboard.features.setup.SetupCheckReportView
import co.maytes.standupbot.dashboard.features.setup.TestMySetupButton
import co.maytes.standupbot.dashboard.features.setup.rememberReconnectLauncher
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberSetupStatusStore
import co.maytes.standupbot.dashboard.styles.HealthStripClasses
import co.maytes.standupbot.dashboard.styles.HomeClasses
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Text

private const val REFRESH_MS = 60_000L
private const val ICON_PX = 12

@Composable
fun HomeHealthStrip(model: HealthStripModel, baseIndex: Int, showCheckReport: Boolean = true) {
    if (model.mode == StripMode.Hidden) return
    val auth = rememberAuthStore()
    RefreshWhileVisible(auth.currentUser?.workspace?.id)
    val stale = rememberSetupStatusStore().state.stale
    HmDiv(HomeClasses.PANEL, HealthStripClasses.ROOT, modifier = Modifier.staggerIn(baseIndex)) {
        StateSwap(state = model.mode, rise = false) {
            HmDiv(modifier = Modifier.stack()) {
                StripHead(model, stale)
                if (model.canAct && model.mode == StripMode.Expanded) {
                    HmDiv(HealthStripClasses.ROWS) { model.chips.forEach { ChipRow(it, model.canAct) } }
                }
            }
        }
        if (showCheckReport) SetupCheckReportView()
    }
}

@Composable
private fun RefreshWhileVisible(workspaceId: String?) {
    val store = rememberSetupStatusStore()
    LaunchedEffect(workspaceId) {
        if (workspaceId.isNullOrBlank()) return@LaunchedEffect
        while (true) {
            delay(REFRESH_MS)
            val hidden = document.asDynamic().hidden == true
            if (!hidden && document.hasFocus()) store.refresh(workspaceId)
        }
    }
}

@Composable
private fun StripHead(model: HealthStripModel, stale: Boolean) {
    val unhealthy = model.unhealthyChips.isNotEmpty()
    HmDiv(HealthStripClasses.HEAD) {
        HmDiv(HomeClasses.MARK, if (unhealthy) HomeClasses.MARK_WARN else HomeClasses.MARK_DONE) {
            if (unhealthy) LucideAlertTriangle(size = ICON_PX) else LucideCheck(size = ICON_PX)
        }
        HmDiv(HealthStripClasses.HEAD_TEXT) {
            HmStrong(if (model.canAct) model.summary else model.memberLine.orEmpty())
            if (stale) HmDiv(HealthStripClasses.STALE) { Text("Status may be out of date") }
        }
        if (model.canAct) HmDiv(HomeClasses.STRIP_END) { TestMySetupButton(idleLabel = "Check now") }
    }
}

@Composable
private fun ChipRow(chip: StripChip, canAct: Boolean) {
    val toneClass =
        when (chip.tone) {
            StripTone.Broken -> HealthStripClasses.ROW_BAD
            StripTone.Degraded -> HealthStripClasses.ROW_WARN
            StripTone.Muted -> HealthStripClasses.ROW_MUTED
            StripTone.Healthy -> null
        }
    HmDiv(
        *classesOf(HealthStripClasses.ROW, toneClass),
        modifier = Modifier.attrsModifier {
            attr("role", "group")
            attr("aria-label", chip.ariaLabel)
        },
    ) {
        HmDiv(HealthStripClasses.NAME) {
            HmStrong(chip.name)
            Badge(label = chip.stateWord, tone = badgeTone(chip.tone))
        }
        HmDiv(HealthStripClasses.FACTS) {
            chip.evidence?.let { HmMuted(it) }
            chip.detail?.let { HmMuted(it) }
            chip.checked?.let { HmMuted(it) }
        }
        if (canAct) chip.action?.let { HmDiv(HealthStripClasses.ACTION) { ChipAction(chip, it) } }
    }
}

@Composable
private fun ChipAction(chip: StripChip, action: StripAction) {
    val auth = rememberAuthStore()
    val launcher = rememberReconnectLauncher()
    val nav = rememberNavigator()
    val scope = rememberCoroutineScope()
    var launching by remember { mutableStateOf(false) }
    Button(
        label = if (launching) "Redirecting…" else action.label,
        variant = if (chip.tone == StripTone.Broken) ButtonVariant.Primary else ButtonVariant.Secondary,
        size = ButtonSize.Sm,
        enabled = !launching,
        onClick = {
            when (action) {
                is StripAction.Open -> openPath(action.path, nav)
                is StripAction.Reconnect -> {
                    val workspaceId = auth.currentUser?.workspace?.id ?: return@Button
                    scope.launch {
                        launching = true
                        val started = launcher.launch(action.provider, workspaceId, chip.name, ReconnectRoutes.HOME, nav)
                        if (!started) launching = false
                    }
                }
            }
        },
    )
}

private fun openPath(path: String, nav: (String) -> Unit) {
    if (path.startsWith("http")) window.open(path, "_blank", "noopener") else nav(path)
}

private fun badgeTone(tone: StripTone): BadgeTone =
    when (tone) {
        StripTone.Healthy -> BadgeTone.Success
        StripTone.Degraded -> BadgeTone.Warning
        StripTone.Broken -> BadgeTone.Error
        StripTone.Muted -> BadgeTone.Neutral
    }
